import type { Route } from '@metro/routing'
import { parseConvenientCars, type ConvenientCarType } from './carTypes'

/**
 * Per-approach convenient-car record from the schema's
 * `transition.convenientTransferCars`. Inlined here (previously imported
 * from the host `schema/types`) so this package is self-contained; the
 * shape is byte-identical to `SchemaData`'s `SchemaConvenientTransferCars`
 * — the host passes those records straight in through
 * {@link ConvenientCarsLookup}.
 */
export interface SchemaConvenientTransferCars {
  /** Station the rider arrived from on the incoming line (the stop
   *  before the alighting station). */
  readonly prevStationId: string
  /** Station the rider alighted at to make the transfer. */
  readonly toStationId: string
  /** SCREAMING_SNAKE car tokens (FIRST / NEAR_FIRST / … / ALL). */
  readonly types: readonly string[]
}

/**
 * Resolves live convenient-transfer cars for a RouteDetails route,
 * keyed by the PRECEDING ride-leg index — the shape
 * `MobileRouteDetailsOverlay`'s `possibleTransfersByLeg` prop expects,
 * which gates S4 (premium car-strip) / S5 (upsell).
 *
 * Web routes locally, so there is no backend `convenient_cars` leg field
 * (the documented platform affordance, QA spec §9). Instead the data is
 * derived here from the bundled schema's per-approach
 * `transition.convenientTransferCars` — the SAME source the backend uses
 * to produce the iOS/Android leg field — so the resolved
 * `ConvenientCarType` set per transfer is CONTRACT-IDENTICAL to
 * iOS/Android (cross-platform lock, 2026-05-25).
 *
 * For each interchange (a transfer/walk leg between two ride legs) the
 * convenient cars apply to the INCOMING (preceding) ride: ride in those
 * cars to minimise the transfer walk (V1 `possibleTransfers` convention).
 * The recommendation depends on the approach — which station you came
 * FROM (the ride's penultimate stop) and where you alighted (its last
 * stop). Lookup order mirrors backend `Resolver.ConvenientCars` (and
 * iOS / Android) exactly:
 *   1. EXACT — entry whose `prevStationId` + `toStationId` equal the
 *      ride's penultimate + last stop.
 *   2. REVERSIBLE (only when `transition.reversible === true`) — entry
 *      whose `prevStationId` equals the ride's penultimate stop (PREV
 *      STAYS FIXED) and whose `toStationId` is the OTHER transition
 *      endpoint (the endpoint flips, the approach does not). Skipped
 *      entirely on a non-reversible transition.
 *   3. DEFAULT — `[NEAR_FIRST, NEAR_END]` (via `parseConvenientCars`).
 */

/**
 * A schema transition's convenient-car data + the fields the reversible
 * tier needs (the `reversible` flag and both endpoints).
 */
export interface ConvenientCarsTransition {
  readonly fromStationId: string
  readonly toStationId: string
  readonly reversible: boolean
  readonly cars: readonly SchemaConvenientTransferCars[]
}

/** Lookup of transition id → its convenient-car data + endpoints/flag. */
export type ConvenientCarsLookup = (transitionId: string) => ConvenientCarsTransition | undefined

interface RideLegRef {
  readonly index: number
  readonly lastStationId: string | undefined
  readonly prevStationId: string | undefined
}

/** The ride legs of a route with their leg index + approach stop ids. */
function rideLegRefs(route: Route): RideLegRef[] {
  const refs: RideLegRef[] = []
  route.legs.forEach((leg, index) => {
    if (leg.type !== 'ride') return
    const stops = leg.stops ?? []
    refs.push({
      index,
      lastStationId: stops[stops.length - 1]?.stationId,
      prevStationId: stops[stops.length - 2]?.stationId,
    })
  })
  return refs
}

/** The interchange leg immediately after `legIndex` — the first non-ride
 *  (transfer/walk) leg, which carries the transition id + any wire cars.
 *  `undefined` when the next leg is a ride (adjacent rides) or none. */
function interchangeLeg(route: Route, legIndex: number): Route['legs'][number] | undefined {
  const next = route.legs[legIndex + 1]
  if (next === undefined || next.type === 'ride') return undefined
  return next
}

/**
 * Picks the matching `types` for a ride approaching a transfer — mirrors
 * backend `Resolver.ConvenientCars` exactly so the output is
 * contract-identical to iOS/Android:
 *   1. EXACT — entry whose `prevStationId` + `toStationId` equal the
 *      ride's penultimate + last stop.
 *   2. REVERSIBLE (only when `transition.reversible === true`) — entry
 *      whose `prevStationId` equals the penultimate stop (PREV STAYS
 *      FIXED) and whose `toStationId` equals the OTHER transition
 *      endpoint (the alighting flips to the transition's far end). On a
 *      non-reversible transition this tier is skipped.
 *   3. `undefined` — no match; caller applies the `[nearFirst, nearEnd]`
 *      parse-default.
 */
function pickTypes(
  transition: ConvenientCarsTransition,
  prevStationId: string | undefined,
  lastStationId: string | undefined,
): readonly string[] | undefined {
  if (lastStationId === undefined) return undefined
  const entries = transition.cars
  const exact = entries.find(
    (e) => e.toStationId === lastStationId && e.prevStationId === prevStationId,
  )
  if (exact !== undefined) return exact.types
  // Reversible tier — gated on the transition's reversible flag. The
  // approach (prevStationId) stays fixed; the alighting endpoint flips to
  // the OTHER end of the transition (from↔to).
  if (!transition.reversible || prevStationId === undefined) return undefined
  const otherEndpoint =
    lastStationId === transition.toStationId
      ? transition.fromStationId
      : lastStationId === transition.fromStationId
        ? transition.toStationId
        : undefined
  if (otherEndpoint === undefined) return undefined
  const reversed = entries.find(
    (e) => e.prevStationId === prevStationId && e.toStationId === otherEndpoint,
  )
  return reversed?.types
}

/**
 * Builds the `possibleTransfersByLeg` map for the route, keyed by the
 * PRECEDING ride-leg index. Dual-source (web has two route origins):
 *   - ONLINE (TFL): the transfer leg already carries the backend-resolved
 *     `convenient_cars` wire field — use it verbatim.
 *   - OFFLINE (local Yen-K): no wire field — derive the equivalent from
 *     the bundled schema's per-approach data via `lookup` + `pickTypes`
 *     (exact → reversible → default), producing identical output.
 * A ride with no following interchange, or one whose interchange has
 * neither wire cars nor schema data, gets no entry (no S4/S5 extra).
 */
export function buildPossibleTransfersByLeg(
  route: Route,
  lookup: ConvenientCarsLookup,
): Record<number, readonly ConvenientCarType[]> {
  const out: Record<number, readonly ConvenientCarType[]> = {}
  for (const ride of rideLegRefs(route)) {
    const leg = interchangeLeg(route, ride.index)
    if (leg === undefined) continue
    // Online: backend already resolved the cars onto the leg.
    if (leg.convenient_cars !== undefined && leg.convenient_cars.length > 0) {
      out[ride.index] = parseConvenientCars(leg.convenient_cars)
      continue
    }
    // Offline: derive from the schema's per-approach data.
    const transitionId = leg.transition_id
    if (transitionId === undefined) continue
    const transition = lookup(transitionId)
    if (transition === undefined || transition.cars.length === 0) continue
    const types = pickTypes(transition, ride.prevStationId, ride.lastStationId)
    // `parseConvenientCars` applies the [nearFirst, nearEnd] default when
    // `types` is undefined/empty — matching the cross-platform fallback.
    out[ride.index] = parseConvenientCars(types ?? [])
  }
  return out
}
